import loadImage from "blueimp-load-image"
//Polyfill
import "blueimp-canvas-to-blob"
import get from "lodash/get"

export const getWidestUncroppedVersion = function (image) {
  const localVersion = get(image.versions, "local")
  if (localVersion) {
    return localVersion
  }

  return getBestImageVersion(image, {
    type: "height",
    minWidth: 1024
  })
}

export const getBestImageVersion = function (image, { type = "square", minWidth = null, minHeight = null }) {
  if (!image?.versions) {
    return {}
  }

  if (!minWidth && !minHeight) {
    throw new Error("At least one of minHeight or minWidth must be positive value")
  }

  const allVersions = getImageVersions(image)
  if (!allVersions.length) {
    return {}
  }

  const versionsForType = allVersions.filter((v) => v.type === type)
  const consideredVersions = versionsForType.length ? versionsForType : allVersions

  return findVersionFittingMeasurements({ versions: consideredVersions, minWidth, minHeight }) || consideredVersions.pop()
}

const getImageVersions = (image) => {
  return Object.keys(image.versions).map((v) => {
    const data = image.versions[v]
    const imageType = v.split("_")[0]
    return {
      type: imageType,
      width: data.width,
      height: data.height,
      url: data.base_url
    }
  })
}

const findVersionFittingMeasurements = ({ versions, minWidth, minHeight }) => {
  return versions.find((v) => {
    if (minWidth && minHeight) {
      return v.width >= minWidth && v.height >= minHeight
    }

    if (minWidth) {
      return v.width >= minWidth
    }

    return v.height >= minHeight
  })
}

const imageFileToBlobProcess = function (blob, imageFile) {
  //Not using File constructor because of MS Edge problems
  blob.name = imageFile.name
}

export const fixOrientation = function (imageFile) {
  return new Promise((resolve) => {
    try {
      const result = loadImage(
        imageFile,
        (canvas) => {
          canvas.toBlob((blob) => {
            imageFileToBlobProcess(blob, imageFile)
            resolve(blob)
          })
        },
        {
          canvas: true,
          //This will fix orientation according to EXIF orientation data
          orientation: true
        }
      )

      if (!result) {
        throw "[loadImage]: Could not load image"
      }
    } catch (err) {
      console.error(err)
      resolve(imageFile)
    }
  })
}

export const resize = function (imageFile, { maxWidth, maxHeight, crop } = {}) {
  return new Promise((resolve) => {
    if (!maxWidth || !maxHeight) {
      console.error("[images.resize]: at least one of maxWidth or maxHeight must be provided")
      resolve(imageFile)
      return
    }
    try {
      const result = loadImage(
        imageFile,
        (canvas) => {
          canvas.toBlob((blob) => {
            imageFileToBlobProcess(blob, imageFile)
            resolve(blob)
          })
        },
        {
          canvas: true,
          maxWidth,
          maxHeight,
          crop
        }
      )

      if (!result) {
        throw "[loadImage]: Could not load image"
      }
    } catch (err) {
      console.error(err)
      resolve(imageFile)
    }
  })
}

const imageUrlForCommentId = {}

export const storeLocalImagesForComment = async ({ images, comment }) => {
  imageUrlForCommentId[comment.id] = await Promise.all(
    images.map(
      (image) =>
        new Promise((resolve, reject) => {
          const fileReader = new FileReader()
          fileReader.onload = () =>
            resolve({
              versions: {
                local: {
                  url: fileReader.result,
                  mime_type: image.type
                }
              }
            })

          fileReader.onerror = () => {
            reject("[loadImage]: Could not load local image")
          }

          fileReader.readAsDataURL(image)
        })
    )
  )
}

export const getCommentWithProcessedImages = (comment) => {
  const localImagesForComment = imageUrlForCommentId[comment.id]

  if (comment.state === "processing") {
    return { ...comment, images: localImagesForComment }
  } else if (localImagesForComment) {
    delete imageUrlForCommentId[comment.id]
  }

  return comment
}

export const validateImageMeasurements = function ({ image, minWidth, minHeight } = {}) {
  return new Promise((resolve) => {
    if (!image) {
      console.error("validateImageMeasurements: image not specified")
      resolve(false)
      return
    }

    image.onload = () => {
      resolve(image.width >= minWidth && image.height >= minHeight)
    }

    image.onerror = () => {
      console.error("validateImageMeasurements: could not load image")
      resolve(false)
    }
  })
}
